/**
 * Universal tagging — thin client for /api/tags.
 * All functions take (instance, accounts) from useMsal, like the rest of the app.
 */

import { authenticatedApiCall } from '../../../utils/apiClient';

export async function fetchTagCategories(instance, accounts, { includeInactive = false } = {}) {
  const r = await authenticatedApiCall(
    instance, accounts, 'GET',
    `/tags/categories${includeInactive ? '?includeInactive=true' : ''}`
  );
  return r.data?.categories || [];
}

export async function fetchAvailableTags(instance, accounts, { entityType, projectId, includeInactive = false } = {}) {
  const params = new URLSearchParams();
  if (entityType) params.set('entityType', entityType);
  if (projectId != null && projectId !== '') params.set('projectId', String(projectId));
  if (includeInactive) params.set('includeInactive', 'true');
  const qs = params.toString();
  const r = await authenticatedApiCall(instance, accounts, 'GET', `/tags${qs ? `?${qs}` : ''}`);
  return r.data?.tags || [];
}

export async function createTag(instance, accounts, { categoryId, name, color, projectId }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags', {
    categoryId, name, color, projectId,
  });
  return r.data?.tag;
}

export async function assignTag(instance, accounts, { tagId, entityType, entityIds, projectId }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags/assign', {
    tagId, entityType, entityIds, projectId,
  });
  return r.data;
}

export async function unassignTag(instance, accounts, { tagId, entityType, entityIds }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags/unassign', {
    tagId, entityType, entityIds,
  });
  return r.data;
}

export async function lookupEntityTags(instance, accounts, { entityType, entityIds }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags/lookup', {
    entityType, entityIds,
  });
  return r.data?.tagsByEntity || {};
}

export async function filterEntitiesByTags(instance, accounts, { entityType, tagIds, mode = 'any' }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags/filter', {
    entityType, tagIds, mode,
  });
  return r.data?.entityIds || [];
}

/** Global explorer: everything carrying a tag, grouped by entity type. */
export async function fetchTagEntities(instance, accounts, tagId) {
  const r = await authenticatedApiCall(instance, accounts, 'GET', `/tags/${tagId}/entities`);
  return r.data; // { tag, total, groups }
}

/** Bulk import: paste-a-list of names into a category. */
export async function bulkCreateTags(instance, accounts, { categoryId, names, projectId, color }) {
  const r = await authenticatedApiCall(instance, accounts, 'POST', '/tags/bulk-create', {
    categoryId, names, projectId, color,
  });
  return r.data; // { created, skippedExisting, invalid }
}

/** Readable text color (white/black) for a given hex background. */
export function contrastText(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return '#1f2937';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  // Perceived luminance (ITU-R BT.601)
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#1f2937' : '#ffffff';
}
